import { useMemo } from 'react';
import styled from '@benzinga/themetron';
import { Ticker } from '@benzinga/ticker-ui';
import { formatPrice } from '@benzinga/utils/numberUtils';

export interface QuoteDataProps {
  absoluteChange: number;
  changePercent: number;
  price: number;
}

export interface QuoteCellProps extends QuoteDataProps {
  anchorDataAction?: string;
  description: string;
  name: string;
  symbol: string;
}

export const SingleLine = styled.span`
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

export const QuoteCell = ({
  absoluteChange,
  anchorDataAction,
  changePercent,
  description,
  name,
  price,
  symbol,
}: QuoteCellProps) => {
  const dataAction = anchorDataAction ?? 'Quote Cell Click';
  return (
    <QuoteCellWrapper className="quote-cell-wrapper" data-action={dataAction} href={`/quote/${symbol.toLowerCase()}`}>
      <div className="quote-name-wrapper">
        <div className="quote-ticker-wrapper">
          <Ticker
            portalElement={document.querySelector('.movers')}
            symbol={symbol}
            targetElement={<div>{symbol}</div>}
          />
        </div>
        <div className="quote-name">
          <SingleLine>{name || description}</SingleLine>
        </div>
      </div>
      <QuoteData {...{ absoluteChange, changePercent, price }} />
    </QuoteCellWrapper>
  );
};

export const QuoteData = ({ absoluteChange, changePercent, price }: QuoteDataProps) => {
  const dir = useMemo(() => {
    if (absoluteChange === 0) return 'none';
    return absoluteChange > 0 ? 'up' : 'down';
  }, [absoluteChange]);
  return (
    <QuoteDataWrapper className="quote-data-wrapper">
      <div className="quote-price">${formatPrice(price)}</div>
      <div className={`quote-change-container ${dir}`}>
        <span className="quote-change-icon">{changeIconMap[dir]}</span>
        <span>
          ${absoluteChange.toFixed(2)} | {changePercent}%
        </span>
      </div>
    </QuoteDataWrapper>
  );
};

const QuoteCellWrapper = styled.a`
  &.quote-cell-wrapper {
    color: black;
    display: flex !important;

    .quote-name-wrapper {
      flex: 1;
    }
    .quote-name {
      color: ${({ theme }) => theme.colors.foregroundMuted};
      font-size: ${({ theme }) => theme.fontSize.sm};
    }
    .quote-ticker-wrapper {
      display: inline-block;
      font-size: ${({ theme }) => theme.fontSize.base};
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }
  }
`;

const QuoteDataWrapper = styled.div`
  &.quote-data-wrapper {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    .quote-price {
      font-size: ${({ theme }) => theme.fontSize.base};
      font-weight: ${({ theme }) => theme.fontWeight.bold};
    }
    .quote-change-icon {
      font-family: 'sarif';
      font-size: ${({ theme }) => theme.fontSize.xs};
      margin-right: 2px;
    }
    .quote-change-container {
      align-items: center;
      color: inherit;
      display: flex;
      font-size: ${({ theme }) => theme.fontSize.sm};
      &.down {
        color: ${({ theme }) => theme.colorPalette.red600};
      }
      &.up {
        color: ${({ theme }) => theme.colorPalette.green600};
      }
    }
  }
`;

const changeIconMap = { down: '▼', none: '', up: '▲' };
// const changeColorMap = { down: 'red600', none: 'inherit', up: 'green600' };
